import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Validates an IANA timezone identifier ("Europe/Kiev", "America/New_York").
 * Uses Intl.DateTimeFormat — anything Node/the browser actually understands
 * passes; typos and bare offsets ("UTC+3") fail.
 */
const IanaTimezoneSchema = z.string().refine(
  (value) => {
    try {
      new Intl.DateTimeFormat("en-US", { timeZone: value });
      return true;
    } catch {
      return false;
    }
  },
  { message: "Invalid IANA timezone" },
);

const DelegateSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
});

const OutOfOfficePeriodSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  delegateUserId: z.string().uuid().nullable(),
  delegate: DelegateSummarySchema.nullable(),
  startsAt: z.string(),
  endsAt: z.string(),
  timezone: z.string(),
  reason: z.string().nullable(),
  cancelledAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const ListInputSchema = z.object({
  /** Defaults to the current user. Looking up another user requires manage-user permission. */
  userId: z.string().uuid().optional(),
  /** When true, includes past and cancelled periods. Defaults to false (current + future, non-cancelled). */
  includeInactive: z.boolean().optional(),
});

const CreateInputSchema = z
  .object({
    userId: z.string().uuid().optional(),
    delegateUserId: z.string().uuid(),
    startsAt: z.string().datetime(),
    endsAt: z.string().datetime(),
    timezone: IanaTimezoneSchema,
    reason: z.string().max(2000).nullable().optional(),
  })
  .refine((v) => new Date(v.endsAt) > new Date(v.startsAt), {
    message: "endsAt must be after startsAt",
    path: ["endsAt"],
  });

const UpdateInputSchema = z.object({
  id: z.string().uuid(),
  delegateUserId: z.string().uuid().optional(),
  startsAt: z.string().datetime().optional(),
  endsAt: z.string().datetime().optional(),
  timezone: IanaTimezoneSchema.optional(),
  reason: z.string().max(2000).nullable().optional(),
});

const CancelInputSchema = z.object({
  id: z.string().uuid(),
});

const ResolveInputSchema = z.object({
  userId: z.string().uuid(),
  /** ISO timestamp; defaults to "now" on the server. */
  atTime: z.string().datetime().optional(),
});

const ResolveOutputSchema = z.object({
  originalUserId: z.string().uuid(),
  effectiveUserId: z.string().uuid(),
  isOutOfOffice: z.boolean(),
  activePeriod: OutOfOfficePeriodSchema.nullable(),
});

export const outOfOfficeContracts = {
  list: oc
    .input(ListInputSchema)
    .output(z.object({ periods: z.array(OutOfOfficePeriodSchema) }))
    .route({ method: "POST" }),

  create: oc
    .input(CreateInputSchema)
    .output(OutOfOfficePeriodSchema)
    .route({ method: "POST" }),

  update: oc
    .input(UpdateInputSchema)
    .output(OutOfOfficePeriodSchema)
    .route({ method: "POST" }),

  cancel: oc
    .input(CancelInputSchema)
    .output(z.object({ success: z.literal(true) }))
    .route({ method: "POST" }),

  resolve: oc
    .input(ResolveInputSchema)
    .output(ResolveOutputSchema)
    .route({ method: "POST" }),
} as const;

export type OutOfOfficePeriodDTO = z.infer<typeof OutOfOfficePeriodSchema>;
export type CreateOutOfOfficeInput = z.infer<typeof CreateInputSchema>;
export type UpdateOutOfOfficeInput = z.infer<typeof UpdateInputSchema>;
export type ResolveOutOfOfficeOutput = z.infer<typeof ResolveOutputSchema>;
